<template>
  <div class="bg-white rounded-lg border border-gray-200 overflow-hidden">
    <div
      class="bg-gray-50 px-4 xl:px-6 py-2 xl:py-3 border-b flex items-center justify-between"
    >
      <h4 class="font-semibold text-sm xl:text-base flex items-center">
        <span class="w-2 h-2 rounded-full mr-2" :class="statusColor"></span>
        {{ title }}
      </h4>
      <button
        @click="copyCode"
        class="flex items-center space-x-1 text-gray-500 hover:text-gray-700 transition-colors duration-200 text-xs xl:text-sm"
        :class="{ 'text-green-600': copied }"
        v-if="request"
      >
        <svg
          v-if="!copied"
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
          ></path>
        </svg>
        <svg
          v-else
          class="w-4 h-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7"
          ></path>
        </svg>
        <span>{{ copied ? "Copiado!" : "Copiar" }}</span>
      </button>
    </div>
    <div class="p-0">
      <pre
        ref="codeBlock"
        :class="['code-block', { 'code-block-xl': isLarge }]"
      ><code :class="`language-${language}`">{{ code }}</code></pre>
    </div>
  </div>
</template>

<script>
export default {
  name: "CodeBlock",
  props: {
    title: {
      type: String,
      required: true,
    },
    code: {
      type: String,
      required: true,
    },
    language: {
      type: String,
      default: "json",
    },
    status: {
      type: String,
      default: "success", // success, error, info
      validator: (value) => ["success", "error", "info"].includes(value),
    },
    isLarge: {
      type: Boolean,
      default: false,
    },
    request: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      copied: false,
    };
  },
  computed: {
    statusColor() {
      const colors = {
        success: "bg-green-500",
        error: "bg-red-500",
        info: "bg-blue-500",
      };
      return colors[this.status];
    },
  },
  methods: {
    async copyCode() {
      try {
        await navigator.clipboard.writeText(this.code);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        // Fallback para navegadores que no soportan clipboard API
        this.fallbackCopyTextToClipboard(this.code);
      }
    },
    fallbackCopyTextToClipboard(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.position = "fixed";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error("Fallback: Could not copy text: ", err);
      }

      document.body.removeChild(textArea);
    },
  },
};
</script>

<style scoped>
.code-block {
  background-color: #1a1a1a;
  color: #f8f8f2;
  padding: 1rem;
  font-size: 8px;
  line-height: 1.5;
  overflow-x: auto;
  margin: 0;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", monospace;
  border-radius: 0;
}

.code-block-xl {
  background-color: #1a1a1a;
  color: #f8f8f2;
  padding: 1.5rem;
  font-size: 13px;
  line-height: 1.6;
  overflow-x: auto;
  margin: 0;
  font-family: "Monaco", "Menlo", "Ubuntu Mono", "Consolas", monospace;
  border-radius: 0;
}

.code-block code,
.code-block-xl code {
  background: none;
  color: inherit;
  font-family: inherit;
  white-space: pre;
}

/* Syntax highlighting */
.language-json .token.string {
  color: #a6e22e;
}

.language-json .token.number {
  color: #ae81ff;
}

.language-json .token.boolean {
  color: #66d9ef;
}

.language-json .token.null {
  color: #66d9ef;
}

.language-json .token.property {
  color: #f92672;
}

.language-json .token.punctuation {
  color: #f8f8f2;
}

/* Custom scrollbar for code blocks */
.code-block::-webkit-scrollbar,
.code-block-xl::-webkit-scrollbar {
  height: 8px;
}

.code-block::-webkit-scrollbar-track,
.code-block-xl::-webkit-scrollbar-track {
  background: #2a2a2a;
}

.code-block::-webkit-scrollbar-thumb,
.code-block-xl::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 4px;
}

.code-block::-webkit-scrollbar-thumb:hover,
.code-block-xl::-webkit-scrollbar-thumb:hover {
  background: #777;
}

/* Responsive adjustments */
@media (max-width: 1380px) {
  .code-block-xl {
    font-size: 10px !important;
    padding: 1.5rem;
    line-height: 1.6;
  }
}
</style>
